import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LandingPage from './components/Landing/LandingPage';
import { SELF_SERVE_BILLING_PLAN } from './components/common/plans';
import FeatureRoute from './components/Dashboard/FeatureRoute';
import AwaitingApprovalPage from './components/Onboarding/AwaitingApprovalPage';

// Route-level code splitting. Before this the whole app shipped as ONE 2.6 MB
// bundle (690 KB gzipped) — a first-time phone visitor to the marketing page
// downloaded and parsed both dashboards, the QR scanner, charts, check-ins and
// finances before the hero text could paint (PageSpeed mobile: LCP 13 s, score
// 50). Only the landing page and the tiny guards stay eager; everything else
// loads when its route is hit. Every module below has a default export.
const StudioWizard = lazy(() => import('./components/Onboarding/StudioWizard'));
const SelfServeStart = lazy(() => import('./components/Onboarding/SelfServeStart'));
const PlanCheckout = lazy(() => import('./components/Onboarding/PlanCheckout'));
const PaymentMethodChoice = lazy(() => import('./components/Onboarding/PaymentMethodChoice'));
const DashboardLayout = lazy(() => import('./components/Dashboard/DashboardLayout'));
const Overview = lazy(() => import('./components/Dashboard/Overview'));
const CoursesPage = lazy(() => import('./components/Dashboard/CoursesPage'));
const StudentsPage = lazy(() => import('./components/Dashboard/StudentsPage'));
const CalendarPage = lazy(() => import('./components/Dashboard/CalendarPage'));
const ClassroomPage = lazy(() => import('./components/Dashboard/ClassroomPage'));
const PaymentsPage = lazy(() => import('./components/Dashboard/PaymentsPage'));
const TuitionPage = lazy(() => import('./components/Dashboard/TuitionPage'));
const FinancesPage = lazy(() => import('./components/Dashboard/FinancesPage'));
const CeoDashboardPage = lazy(() => import('./components/Dashboard/CeoDashboardPage'));
const GrantsPage = lazy(() => import('./components/Dashboard/GrantsPage'));
const MarketingPage = lazy(() => import('./components/Dashboard/MarketingPage'));
const NotificationsSettings = lazy(() => import('./components/Dashboard/NotificationsSettings'));
const SettingsPage = lazy(() => import('./components/Dashboard/SettingsPage'));
const FlowsPage = lazy(() => import('./components/Dashboard/FlowsPage'));
const RoadmapPage = lazy(() => import('./components/Dashboard/RoadmapPage'));
const CheckinsPage = lazy(() => import('./components/Dashboard/CheckinsPage'));
const StudentCheckinsPage = lazy(() => import('./components/StudentDashboard/StudentCheckinsPage'));
const StudioPublicPage = lazy(() => import('./components/Studio/StudioPublicPage'));
const StudioBookingPage = lazy(() => import('./components/Studio/StudioBookingPage'));
const StudentGuidePage = lazy(() => import('./components/Studio/StudentGuidePage'));
const TrialClassPage = lazy(() => import('./components/Trial/TrialClassPage'));
const EnrollmentPage = lazy(() => import('./components/Enrollment/EnrollmentPage'));
const ZelleJoinPage = lazy(() => import('./components/Enrollment/ZelleJoinPage'));
const TuitionPayPage = lazy(() => import('./components/Enrollment/TuitionPayPage'));
const EnrollmentSuccess = lazy(() => import('./components/Enrollment/EnrollmentSuccess'));
const StudentDashboardLayout = lazy(() => import('./components/StudentDashboard/StudentDashboardLayout'));
const StudentOverview = lazy(() => import('./components/StudentDashboard/StudentOverview'));
const MyCourses = lazy(() => import('./components/StudentDashboard/MyCourses'));
const CourseDetail = lazy(() => import('./components/StudentDashboard/CourseDetail'));
const StudentCalendar = lazy(() => import('./components/StudentDashboard/StudentCalendar'));
const MaterialsHub = lazy(() => import('./components/StudentDashboard/MaterialsHub'));
const StudentClassroom = lazy(() => import('./components/StudentDashboard/StudentClassroom'));
const MyPayments = lazy(() => import('./components/StudentDashboard/MyPayments'));
const StudentProfile = lazy(() => import('./components/StudentDashboard/StudentProfile'));
const QRScanPage = lazy(() => import('./components/StudentDashboard/QRScanPage'));
const AttendPage = lazy(() => import('./components/StudentDashboard/AttendPage'));
const AdminTeachersPage = lazy(() => import('./components/Admin/AdminTeachersPage'));
const BlogAdminPage = lazy(() => import('./components/Admin/BlogAdminPage'));
const AdminCallsPage = lazy(() => import('./components/Admin/AdminCallsPage'));
const AdminFeatureRequestsPage = lazy(() => import('./components/Admin/AdminFeatureRequestsPage'));
const AdminRoadmapPage = lazy(() => import('./components/Admin/AdminRoadmapPage'));
const AdminLoginPage = lazy(() => import('./components/Admin/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./components/Admin/AdminDashboardPage'));
const BlogIndex = lazy(() => import('./components/Blog/BlogIndex'));
const BlogArticle = lazy(() => import('./components/Blog/BlogArticle'));
const GrantMatcher = lazy(() => import('./components/Grants/GrantMatcher'));
const GrantApplierPage = lazy(() => import('./components/Grants/GrantApplierPage'));
const TcsBuilderPage = lazy(() => import('./components/TcsBuilder/TcsBuilderPage'));
const PublicAcceptPage = lazy(() => import('./components/TcsBuilder/PublicAcceptPage'));
const MarketingCoachPage = lazy(() => import('./components/Marketing/MarketingCoachPage'));
const LegalPage = lazy(() => import('./components/Legal/LegalPage'));
const NykfOfferPage = lazy(() => import('./components/Festival/NykfOfferPage'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return isAuthenticated ? children : <Navigate to="/" />;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-gray-400 text-sm">Loading...</div>
  </div>
);

const NoEnrollment = () => {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Courses Yet</h2>
        <p className="text-gray-500 mb-6">
          You haven't enrolled in any courses yet. Once you enroll in a course through a studio, your dashboard will appear here.
        </p>
        <button
          onClick={logout}
          className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
        >
          Log out
        </button>
      </div>
    </div>
  );
};

const RootRedirect = () => {
  const { isAuthenticated, user, studio, loading } = useAuth();

  if (!isAuthenticated) return <LandingPage />;

  // Wait for user data before routing
  if (loading || !user) return <LoadingScreen />;

  if (user.role === 'student') {
    return user.studioId ? <Navigate to="/student" /> : <NoEnrollment />;
  }

  // Teacher — block pending/rejected before any onboarding redirect
  if (user.status === 'pending') return <AwaitingApprovalPage />;
  if (user.status === 'rejected') return <AwaitingApprovalPage rejected />;

  return studio ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding/studio" />;
};

const App = () => {
  const { loading } = useAuth();

  if (loading) return <LoadingScreen />;

  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<RootRedirect />} />

      {/* Self-serve entry — PUBLIC on purpose: it is where a signed-out visitor
          creates an account, so it must not sit behind ProtectedRoute. The plan
          rides in the URL so the approval-gate bypass below can see it. */}
      <Route path="/start" element={<SelfServeStart />} />
      <Route path="/start/:planKey" element={<SelfServeStart />} />

      {/* Onboarding (teachers only) */}
      <Route
        path="/onboarding/studio"
        element={
          <ProtectedRoute>
            <OnboardingGuard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/plan"
        element={
          <ProtectedRoute>
            <PlanCheckout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/payments"
        element={
          <ProtectedRoute>
            <PaymentMethodChoice />
          </ProtectedRoute>
        }
      />

      {/* Teacher Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <TeacherDashboardGuard />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/new" element={<CoursesPage creating />} />
        <Route path="materials" element={<Navigate to="/dashboard/students?tab=materials" />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="calendar" element={<FeatureRoute feature="calendar"><CalendarPage /></FeatureRoute>} />
        <Route path="attendance" element={<Navigate to="/dashboard/students?tab=attendance" />} />
        <Route path="classroom" element={<FeatureRoute feature="classroom"><ClassroomPage /></FeatureRoute>} />
        <Route path="payments" element={<PaymentsPage />} />
        {/* Zelle-only ledger; the page itself redirects a Stripe studio away. */}
        <Route path="tuition" element={<TuitionPage />} />
        <Route path="finances" element={<FinancesPage />} />
        <Route path="revenue" element={<FeatureRoute feature="ceo_dashboard"><CeoDashboardPage /></FeatureRoute>} />
        <Route path="grants" element={<GrantsPage />} />
        <Route path="grants/:grantId" element={<GrantsPage />} />
        <Route path="tcs" element={<TcsBuilderPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="flows" element={<FeatureRoute feature="flows"><FlowsPage /></FeatureRoute>} />
        <Route path="checkins" element={<FeatureRoute feature="checkins"><CheckinsPage /></FeatureRoute>} />
        <Route path="notifications" element={<FeatureRoute feature="announcements"><NotificationsSettings /></FeatureRoute>} />
        <Route path="roadmap" element={<FeatureRoute feature="roadmap"><RoadmapPage /></FeatureRoute>} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<Navigate to="/dashboard/settings" />} />
      </Route>

      {/* Student Dashboard */}
      <Route
        path="/student"
        element={
          <ProtectedRoute>
            <StudentDashboardGuard />
          </ProtectedRoute>
        }
      >
        <Route index element={<StudentOverview />} />
        <Route path="courses" element={<MyCourses />} />
        <Route path="course/:courseId" element={<CourseDetail />} />
        <Route path="calendar" element={<StudentCalendar />} />
        <Route path="materials" element={<MaterialsHub />} />
        <Route path="classroom" element={<StudentClassroom />} />
        <Route path="payments" element={<MyPayments />} />
        <Route path="scan" element={<QRScanPage />} />
        <Route path="checkins" element={<StudentCheckinsPage />} />
        <Route path="profile" element={<StudentProfile />} />
      </Route>

      {/* Admin */}
      {/* /admin-login is public — it renders the right thing for signed-out,
          non-admin, and admin states itself. */}
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teachers"
        element={
          <ProtectedRoute>
            <AdminTeachersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <ProtectedRoute>
            <BlogAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/calls"
        element={
          <ProtectedRoute>
            <AdminCallsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/features"
        element={
          <ProtectedRoute>
            <AdminFeatureRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roadmap"
        element={
          <ProtectedRoute>
            <AdminRoadmapPage />
          </ProtectedRoute>
        }
      />

      {/* Public marketing blog */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />

      {/* Public Grant Matcher (main tool). /grants/match kept as a redirect. */}
      <Route path="/grants" element={<GrantMatcher />} />
      <Route path="/grants/match" element={<Navigate to="/grants" replace />} />
      {/* Public NY Kathak Festival offer page (QR tote-card campaign) */}
      <Route path="/nykf" element={<NykfOfferPage />} />

      {/* Public AI Grant Applier (standalone — applier-token identity, no login) */}
      <Route path="/grants/apply" element={<GrantApplierPage />} />
      <Route path="/grants/apply/:grantId" element={<GrantApplierPage />} />
      {/* Public T&Cs acceptance link (from acceptance-request emails) */}
      <Route path="/tcs/accept/:token" element={<PublicAcceptPage />} />
      {/* Public Marketing Coach lead magnet */}
      <Route path="/marketing-coach" element={<MarketingCoachPage />} />
      {/* DanceHall's own Terms of Service + Privacy Policy (public, never gated) */}
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      {/* Public NY Kathak Festival offer page (QR tote-card campaign) */}
      <Route path="/nykf" element={<NykfOfferPage />} />

      {/* Public studio page + enrollment */}
      <Route path="/s/:slug" element={<StudioPublicPage />} />
      {/* The Instagram-bio link. A separate route from /s/:slug, which is a sign-in
          page some studios already share. */}
      <Route path="/s/:slug/book" element={<StudioBookingPage />} />
      <Route path="/s/:slug/guide" element={<StudentGuidePage />} />
      <Route path="/s/:slug/trial" element={<TrialClassPage />} />
      <Route path="/s/:slug/enroll/:courseId" element={<EnrollmentPage />} />
      {/* Zelle studios: sign up without a card. EnrollmentPage redirects here. */}
      <Route path="/s/:slug/join/:courseId" element={<ZelleJoinPage />} />
      {/* Where the monthly Zelle payment email lands. Public — the signed token is
          the authority, and requiring a login to say "I paid" guarantees nobody does. */}
      <Route path="/pay/:token" element={<TuitionPayPage />} />
      {/* Attendance QR landing — the QR encodes this URL so a phone-camera scan
          works. Public: it signs the student in itself, then marks attendance. */}
      <Route path="/attend" element={<AttendPage />} />

      {/* Enrollment success */}
      <Route
        path="/enrollment/success"
        element={
          <ProtectedRoute>
            <EnrollmentSuccess />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
};

// Guard: only teachers without a studio see onboarding; others redirect
const OnboardingGuard = () => {
  const { user, studio } = useAuth();

  // Self-serve arrives as ?plan=studio_flat (set by /start/:planKey). Those signups
  // skip the manual review screen — the $49 card promises "Self-serve. No call
  // needed.", and a brand-new teacher is status:'pending' until their studio is
  // created, so without this the flow dead-ends at "Your account is under review"
  // immediately after signup. The server applies the same bypass on POST /studio
  // and approves them there; this is the UI half of that one decision.
  //
  // 'rejected' is NOT bypassed — that is a deliberate no.
  const params = new URLSearchParams(window.location.search);
  // Two link-driven signups skip review: the $49 tier (?plan=studio_flat) and the
  // custom tier (?signup=custom), which is handed out per deal from /start/custom.
  const selfServe = params.get('plan') === SELF_SERVE_BILLING_PLAN
    || params.get('signup') === 'custom';

  if (user?.role === 'student') return <Navigate to="/student" />;
  if (user?.status === 'rejected') return <AwaitingApprovalPage rejected />;
  if (user?.status === 'pending' && !selfServe) return <AwaitingApprovalPage />;
  return studio ? <Navigate to="/dashboard" /> : <StudioWizard />;
};

// Guard: only teachers with a studio see the dashboard
const TeacherDashboardGuard = () => {
  const { user, studio } = useAuth();

  if (user?.role === 'student') return <Navigate to="/student" />;
  if (!studio) return <Navigate to="/onboarding/studio" />;

  // Self-serve studios must start their trial (card on file) before the dashboard.
  // Scoped to the flat plan so it can never strand an existing studio: TFATX and
  // HipHop-Natyam are on `growth` with no platformSubscription, and are untouched.
  // A studio that abandons at the plan step lands back here on next login rather
  // than getting the product for free.
  const onFlatPlan = studio.plan === SELF_SERVE_BILLING_PLAN;
  const needsPlan = onFlatPlan
    && !['trialing', 'active'].includes(studio.platformSubscription?.status);
  if (needsPlan) return <Navigate to="/onboarding/plan" replace />;

  // Trial started but they haven't said how they collect tuition. Asked once, right
  // after signup, because it decides what every student invite says. Growth-plan
  // studios are never asked — they are already on the 6% destination path.
  if (onFlatPlan && !studio.paymentCollection?.method) {
    return <Navigate to="/onboarding/payments" replace />;
  }

  return <DashboardLayout />;
};

// Guard: only students see the student dashboard
const StudentDashboardGuard = () => {
  const { user } = useAuth();

  if (user?.role === 'teacher') return <Navigate to="/dashboard" />;
  return user?.studioId ? <StudentDashboardLayout /> : <Navigate to="/" />;
};

export default App;
